// src\themes\default\components\OtpVerificationLoginStep.tsx
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useRouter, useSearchParams } from 'next/navigation';

import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton")
import { useAuth } from '@/contexts/authContext';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { verifyCode } from '@/core/lib/api/main/auth';
import {
    getOtpVerificationStepSchema,
    OtpVerificationStepSchemaData,
} from '@/core/schemas/authSchema';
import { OtpVerificationStepProps } from '@/core/types/auth';
import { InputOTPPattern } from '@/components/main/login/InputOTPPattern';

// اضافه کردن props برای setStep و goBack
interface ExtendedOtpVerificationStepProps extends OtpVerificationStepProps {
    setStep: (step: 1 | 2 | 3) => void;
    goBack: () => void;
}

export default function OtpVerificationLoginStep({
    mobile,
    session_token,
    timeLeft,
    handleResend,
    goBack,
}: ExtendedOtpVerificationStepProps) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { setAuth } = useAuth();
    const { dictionary } = useDictionary();
    const otpSchema = getOtpVerificationStepSchema(dictionary);

    const {
        control,
        handleSubmit,
        watch,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<OtpVerificationStepSchemaData>({
        resolver: zodResolver(otpSchema),
        defaultValues: {
            verificationCode: '',
        },
    });

    const verificationCode = watch('verificationCode');

    useEffect(() => {
        if (verificationCode && verificationCode.length === 6) {
            const timer = setTimeout(() => {
                handleSubmit(onSubmit)();
            }, 300);

            return () => clearTimeout(timer); // cleanup
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verificationCode, handleSubmit]);

    const formatTime = () => {
        const minutes = Math.floor(timeLeft / 60);
        const seconds = timeLeft % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const onSubmit = async (data: OtpVerificationStepSchemaData) => {
        try {
            const result = await verifyCode({
                mobile,
                session_token,
                verifyCode: data.verificationCode,
            });

            if (result?.status === 'success') {
                setAuth(result.data);
                toast.success(result.message);

                const returnUrl = searchParams.get('returnUrl');
                router.push(returnUrl ? decodeURIComponent(returnUrl) : '/dashboard');

                // سینک کردن سبد خرید دیگه لازم نیست جلوی رفتن به داشبورد رو بگیره
                useCartStore.getState().syncCart(true)
                    .then(() => useCartStore.getState().fetchCartFromBackend())
                    .catch((err) => console.error('Cart sync error:', err));

            } else {
                setError('verificationCode', { type: 'server', message: result.message });
            }
        } catch (error) {
            console.error('OTP verification error:', error);
            toast.error(dictionary.common?.error);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
            <div className="flex flex-col w-full mb-8 h-24">
                <InputOTPPattern control={control} errors={errors} />
            </div>

            <CustomButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                size="full"
            >
                {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    dictionary.forms?.login
                )}
            </CustomButton>

            <div className="grid grid-cols-2 justify-between mt-2">
                <div>
                    {timeLeft > 0 ? (
                        <div className="flex gap-x-2">
                            <p className="select-none text-primary-600 w-fit ltr:order-2">
                                {formatTime()}
                            </p>
                            <p className="select-none text-primary-600 w-fit">
                                {dictionary.forms?.resendCode}
                            </p>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={handleResend}
                            className="cursor-pointer text-primary-600"
                        >
                            {dictionary.forms?.resendOtp}
                        </button>
                    )}
                </div>

                <button
                    type="button"
                    onClick={goBack}
                    className="text-primary-600 w-fit cursor-pointer justify-self-end"
                >
                    {dictionary.forms?.loginWithPassword}
                </button>
            </div>
        </form>
    );
}